/**
 * Dashboard Constants
 * Centralized constants for Dashboard and related components
 */

// Search Types
export const SEARCH_TYPES = {
  ALL: '',
  TEAM: 'team',
  PROJECT: 'project',
  FEATURE: 'feature',
  TEST_CASE: 'testcase',
  BUG: 'bug',
  TEST_RUN: 'testrun'
}

// Search Type Options for Dropdown
export const SEARCH_TYPE_OPTIONS = [
  { value: SEARCH_TYPES.ALL, label: 'All' },
  { value: SEARCH_TYPES.TEAM, label: 'Teams' },
  { value: SEARCH_TYPES.PROJECT, label: 'Projects' },
  { value: SEARCH_TYPES.FEATURE, label: 'Features' },
  { value: SEARCH_TYPES.TEST_CASE, label: 'Test Cases' },
  { value: SEARCH_TYPES.BUG, label: 'Bugs' },
  { value: SEARCH_TYPES.TEST_RUN, label: 'Test Runs' }
]

// Search Type Labels
export const SEARCH_TYPE_LABELS = {
  [SEARCH_TYPES.TEAM]: 'Team',
  [SEARCH_TYPES.PROJECT]: 'Project',
  [SEARCH_TYPES.FEATURE]: 'Feature',
  [SEARCH_TYPES.TEST_CASE]: 'Test Case',
  [SEARCH_TYPES.BUG]: 'Bug',
  [SEARCH_TYPES.TEST_RUN]: 'Test Run'
}

// Stat Card Icons (SVG path data)
// Icons represent each entity type in the dashboard
export const STAT_CARD_ICONS = {
  TEAMS: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  PROJECTS: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
  FEATURES: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0a4 4 0 004-4v-4a2 2 0 012-2h4a2 2 0 012 2v4a4 4 0 01-4 4h-8z',
  TEST_CASES: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  TEST_RUNS: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  BUGS: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
}

// Stat Card Color Schemes (matching badge colors for consistency)
// Using light, subtle gradients for a clean modern look
export const STAT_CARD_COLORS = {
  TEAMS: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    bg: 'bg-yellow-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-yellow-600',
    darkText: 'dark:text-yellow-400',
    icon: STAT_CARD_ICONS.TEAMS,
    gradient: 'from-yellow-50/50 to-yellow-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-yellow-200 dark:border-yellow-500',
    textLabel: 'text-yellow-700 dark:text-yellow-300',
    textValue: 'text-yellow-800 dark:text-yellow-200',
    textSubtext: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/40',
    iconColor: 'text-yellow-500 dark:text-yellow-400',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  },
  PROJECTS: {
    from: 'from-blue-500',
    to: 'to-blue-600',
    bg: 'bg-blue-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-blue-600',
    darkText: 'dark:text-blue-300',
    icon: STAT_CARD_ICONS.PROJECTS,
    gradient: 'from-blue-50/50 to-blue-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-blue-200 dark:border-blue-500',
    textLabel: 'text-blue-700 dark:text-blue-300',
    textValue: 'text-blue-800 dark:text-blue-200',
    textSubtext: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/40',
    iconColor: 'text-blue-500 dark:text-blue-400',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  },
  FEATURES: {
    from: 'from-cyan-500',
    to: 'to-cyan-600',
    bg: 'bg-cyan-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-cyan-600',
    darkText: 'dark:text-cyan-300',
    icon: STAT_CARD_ICONS.FEATURES,
    gradient: 'from-cyan-50/50 to-cyan-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-cyan-200 dark:border-cyan-500',
    textLabel: 'text-cyan-700 dark:text-cyan-300',
    textValue: 'text-cyan-800 dark:text-cyan-200',
    textSubtext: 'text-cyan-600 dark:text-cyan-400',
    iconBg: 'bg-cyan-50 dark:bg-cyan-900/40',
    iconColor: 'text-cyan-500 dark:text-cyan-400',
    badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300'
  },
  TEST_CASES: {
    from: 'from-emerald-500',
    to: 'to-emerald-600',
    bg: 'bg-emerald-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-emerald-600',
    darkText: 'dark:text-emerald-300',
    icon: STAT_CARD_ICONS.TEST_CASES,
    gradient: 'from-emerald-50/50 to-emerald-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-emerald-200 dark:border-emerald-500',
    textLabel: 'text-emerald-700 dark:text-emerald-300',
    textValue: 'text-emerald-800 dark:text-emerald-200',
    textSubtext: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/40',
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
  },
  TEST_RUNS: {
    from: 'from-purple-500',
    to: 'to-purple-600',
    bg: 'bg-purple-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-purple-600',
    darkText: 'dark:text-purple-300',
    icon: STAT_CARD_ICONS.TEST_RUNS,
    gradient: 'from-purple-50/50 to-purple-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-purple-200 dark:border-purple-500',
    textLabel: 'text-purple-700 dark:text-purple-300',
    textValue: 'text-purple-800 dark:text-purple-200',
    textSubtext: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/40',
    iconColor: 'text-purple-500 dark:text-purple-400',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
  },
  BUGS: {
    from: 'from-red-500',
    to: 'to-red-600',
    bg: 'bg-red-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-red-600',
    darkText: 'dark:text-red-300',
    icon: STAT_CARD_ICONS.BUGS,
    gradient: 'from-red-50/50 to-red-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-red-200 dark:border-red-500',
    textLabel: 'text-red-700 dark:text-red-300',
    textValue: 'text-red-800 dark:text-red-200',
    textSubtext: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/40',
    iconColor: 'text-red-500 dark:text-red-400',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  },
  SETTINGS: {
    from: 'from-gray-500',
    to: 'to-gray-600',
    bg: 'bg-gray-50',
    darkBg: 'dark:bg-slate-800',
    text: 'text-gray-600',
    darkText: 'dark:text-gray-300',
    icon: STAT_CARD_ICONS.TEAMS,
    gradient: 'from-gray-50/50 to-gray-50 dark:from-slate-800 dark:to-slate-800',
    border: 'border-gray-200 dark:border-gray-500',
    textLabel: 'text-gray-700 dark:text-gray-300',
    textValue: 'text-gray-800 dark:text-gray-200',
    textSubtext: 'text-gray-600 dark:text-gray-400',
    iconBg: 'bg-gray-50 dark:bg-gray-900/40',
    iconColor: 'text-gray-500 dark:text-gray-400',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
  }
}

// Project Status Colors (for visual indicators)
export const PROJECT_STATUS_COLORS = {
  active: 'bg-success-500',
  completed: 'bg-gray-500',
  archived: 'bg-gray-400',
  paused: 'bg-warning-500'
}

// Default Empty State Messages
export const EMPTY_STATE_MESSAGES = {
  NO_TEAMS: 'No teams found',
  NO_PROJECTS: 'No projects in this team',
  NO_SEARCH_RESULTS: 'No results found',
  NO_DATA: 'No data available'
}

// Animation Durations
export const ANIMATION_DURATIONS = {
  HOVER: 'duration-300',
  TRANSITION: 'duration-200',
  SMOOTH_SCROLL: 'smooth'
}

